import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Lock, User, ArrowLeft, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useSettings } from "@/contexts/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const settings = useSettings();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("يرجى إدخال اسم المستخدم وكلمة المرور");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem("admin_token", data.token);
      localStorage.setItem("admin_name", data.name || data.username);
      toast.success("تم تسجيل الدخول بنجاح");
      navigate("/admin/dashboard");
    } catch {
      toast.error("بيانات الدخول غير صحيحة");
    }
    setLoading(false);
  };

  const logoUrl = settings.logo_dark_url || settings.logo_url;

  return (
    <div dir="rtl" className="min-h-screen relative overflow-hidden flex items-center justify-center px-4" data-testid="admin-login-page"
      style={{ background: "linear-gradient(135deg, #0a0a0b 0%, #111115 40%, #0d0d12 100%)" }}>

      {/* Home button */}
      <Link
        to="/"
        className="absolute top-6 right-6 z-20 flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-zinc-400 hover:text-white hover:bg-white/10 transition-all text-sm font-medium backdrop-blur-sm"
        data-testid="login-home-btn"
      >
        <Home size={16} />
        الصفحة الرئيسية
      </Link>

      {/* Massive background watermark - logo image or site name */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none" aria-hidden="true">
        {logoUrl ? (
          <img src={logoUrl} alt="" className="w-[55vw] h-[55vh] object-contain opacity-[0.08]"
            style={{ filter: "brightness(1.8)" }} />
        ) : (
          <span className="font-['Zain'] font-black text-[30vw] md:text-[35vw] whitespace-nowrap"
            style={{
              background: `linear-gradient(135deg, hsl(var(--primary) / 0.06), hsl(var(--gradient-end) / 0.08))`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              lineHeight: 1
            }}>
            {settings.site_name || "رؤية"}
          </span>
        )}
      </div>

      {/* Animated gradient orbs */}
      <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full blur-[180px] animate-pulse"
        style={{ background: `radial-gradient(circle, hsl(var(--primary) / 0.08), transparent 70%)` }} />
      <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] rounded-full blur-[150px] animate-pulse"
        style={{ background: `radial-gradient(circle, hsl(var(--gradient-end) / 0.06), transparent 70%)`, animationDelay: "1s" }} />

      {/* Grid pattern */}
      <div className="absolute inset-0 bg-grid opacity-30" />

      {/* Subtle grain overlay */}
      <div className="absolute inset-0 grain" />

      {/* Top glow line */}
      <div className="absolute top-0 left-0 right-0 h-[1px]"
        style={{ background: `linear-gradient(90deg, transparent, hsl(var(--primary) / 0.3), hsl(var(--gradient-end) / 0.3), transparent)` }} />

      {/* Main content */}
      <div className="relative z-10 w-full max-w-md">
        {/* Logo / Branding */}
        <div className="text-center mb-10">
          {logoUrl ? (
            <img src={logoUrl} alt={settings.site_name} className="h-14 md:h-16 mx-auto mb-4 object-contain" data-testid="login-logo-img" />
          ) : (
            <h1 className="text-4xl md:text-5xl font-black font-['Zain'] mb-3" data-testid="login-logo-text"
              style={{
                background: `linear-gradient(135deg, hsl(var(--primary)), hsl(var(--gradient-end)))`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text"
              }}>
              {settings.site_name || "رؤية"}
            </h1>
          )}
          <p className="text-zinc-500 text-lg font-['Zain'] font-medium tracking-wide" data-testid="login-tagline">
            أكثر مما تتخيل
          </p>
        </div>

        {/* Login Card */}
        <form onSubmit={handleLogin}
          className="relative backdrop-blur-xl rounded-2xl p-7 md:p-9 space-y-6 border"
          style={{
            background: "rgba(22, 22, 26, 0.6)",
            borderColor: "rgba(255,255,255,0.06)",
            boxShadow: "0 25px 60px -15px rgba(0,0,0,0.5), inset 0 1px 0 0 rgba(255,255,255,0.04)"
          }}
          data-testid="admin-login-form">

          {/* Form glow accent */}
          <div className="absolute -top-[1px] left-1/4 right-1/4 h-[1px]"
            style={{ background: `linear-gradient(90deg, transparent, hsl(var(--primary) / 0.5), hsl(var(--gradient-end) / 0.5), transparent)` }} />

          <div>
            <label className="text-zinc-400 text-sm mb-2 block font-medium">اسم المستخدم</label>
            <div className="relative">
              <User size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-600" />
              <Input
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="bg-white/[0.04] border-white/[0.08] text-white pr-10 text-right h-12 rounded-xl focus:border-primary/50 transition-colors placeholder:text-zinc-600"
                placeholder="اسم المستخدم"
                data-testid="admin-username"
              />
            </div>
          </div>

          <div>
            <label className="text-zinc-400 text-sm mb-2 block font-medium">كلمة المرور</label>
            <div className="relative">
              <Lock size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-600" />
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="bg-white/[0.04] border-white/[0.08] text-white pr-10 text-right h-12 rounded-xl focus:border-primary/50 transition-colors placeholder:text-zinc-600"
                placeholder="••••••••"
                data-testid="admin-password"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-xl text-white text-base font-semibold relative overflow-hidden group"
            style={{
              background: `linear-gradient(135deg, hsl(var(--primary)), hsl(var(--gradient-end)))`,
            }}
            data-testid="admin-login-submit"
          >
            <span className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-all duration-300" />
            <span className="relative flex items-center justify-center gap-2">
              {loading ? "جاري الدخول..." : "تسجيل الدخول"}
              {!loading && <ArrowLeft size={16} />}
            </span>
          </Button>
        </form>

        <p className="text-center text-zinc-700 text-xs mt-5">&nbsp;</p>
      </div>
    </div>
  );
}
